import { useState, useEffect } from 'react';

// R4: Import and Export Components — "Successfully import and export components 
// between individual files. Ensure that imported components can be visible onto 
// the screen." App.js imports all child components from their own files 
// and renders them onto the screen based on the current view state.
import Signup from './Signup';
import Login from './Login';
import Landing from './Landing';
import Dashboard from './Dashboard';
import './App.css';
import Nav from './Nav';
import Folders from './Folders';
import AllPins from './AllPins';

// R1: Functional Components — "Create at least 2 functional components and render 
// them onto the screen." App is the root functional component — a plain JavaScript 
// function that returns JSX and renders the correct child component onto the screen 
// based on which view is active.

// R2: Passing Props to Components — "Create a parent component that passes props 
// to child components. Ensure that the child components can access and display the 
// passed props." App is the parent component. It passes props down to Login, 
// Signup, Dashboard, Folders, and Nav below.
function App() {

  // R6: React Hooks — useState is a React Hook that gives App memory. 
  // view tracks which screen to show. userData stores the logged-in user's data.
  const [view, setView] = useState('landing');
  const [userData, setUserData] = useState(null);

  // R6: React Hooks — useEffect checks localStorage on page load so the user
  // stays logged in after a browser refresh, and restores the last view they were on.
  useEffect(() => {
    const stored = localStorage.getItem('pinbook_user');
    const storedView = localStorage.getItem('pinbook_view');
    if (stored) {
      const parsed = JSON.parse(stored);
      setUserData(parsed);
      setView(storedView || 'dashboard');
    }
  }, []);

  // R2: Passing Props — handleLoginSuccess is passed to Login as the 
  // onLoginSuccess prop. When Login calls it, App stores the user data,
  // persists it to localStorage, and switches to the dashboard view.
  function handleLoginSuccess(data) {
    setUserData(data);
    setView('dashboard');
    localStorage.setItem('pinbook_user', JSON.stringify(data));
    localStorage.setItem('pinbook_view', 'dashboard');
  }

  // R2: Passing Props — handleLogout is called via the Nav component's 
  // onNavigate prop. Clears user data and view from state and localStorage.
  function handleLogout() {
    setUserData(null);
    setView('login');
    localStorage.removeItem('pinbook_user');
    localStorage.removeItem('pinbook_view');
  }

  return (
    <div className="App">

      {/* R3: Style Components using CSS — "Style your react app with CSS." 
          The className="App" references a CSS class defined in App.css which 
          is imported at the top of this file. */}

      {/* R2: Passing Props — Nav receives onNavigate and currentView as props 
          from App. onNavigate handles both logout and view switching, and 
          persists the current view to localStorage. */}
      {userData && (
        <Nav
          onNavigate={(v) => {
            if (v === 'logout') {
              handleLogout();
            } else {
              setView(v);
              localStorage.setItem('pinbook_view', v);
            }
          }}
          currentView={view}
        />
      )}

      {/* R2: Passing Props — App passes onLoginSuccess and onGoToSignup 
          as props to Login. Login receives and uses both to communicate 
          back to App when events occur. */}

      {view === 'landing' && (
        <Landing
          onGetStarted={() => setView('signup')}
          onLogin={() => setView('login')}
        />
      )}

      {view === 'login' && (
        <Login
          onLoginSuccess={handleLoginSuccess}
          onGoToSignup={() => setView('signup')}
        />
      )}

      {/* R2: Passing Props — App passes onGoToLogin as a prop to Signup. 
          Signup calls it when the user wants to switch to the login view. */}
      {view === 'signup' && (
        <Signup onGoToLogin={() => setView('login')} />
      )}

      {/* R2: Passing Props — App passes userData as a prop to Dashboard. 
          Dashboard receives it and displays the user's pinbook address 
          and pins, satisfying the requirement that child components can 
          access and display passed props. */}
      {view === 'dashboard' && (
        <Dashboard userData={userData} />
      )}

      {/* R2: Passing Props — App passes userData as a prop to Folders. 
          Folders uses userData.token to authenticate all API requests. */}
      {view === 'folders' && (
        <Folders userData={userData} />
      )}

      {view === 'pins' && (
        <AllPins userData={userData} />
      )}

    </div>
  );
}

// R4: Import and Export Components — export default makes App available 
// to index.js which imports it and renders it as the root of the entire 
// React application onto the screen.
export default App;